package app.footyguide.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*

/**
 * Reusable Image component with fallback logic.
 * Ensures that broken links don't show a generic browser 'broken image' icon.
 */
@Composable
fun SafeImg(
    src: String?,
    alt: String = "",
    fallbackSrc: String? = null,
    fallbackIcon: String = Icons.SHIELD,
    fallbackSize: Int = 24,
    loading: String = "lazy",
    modifier: (StyleScope.() -> Unit)? = null
) {
    var currentSrc by remember(src) { mutableStateOf(src) }
    var hasError by remember(src) { mutableStateOf(false) }
    val effectiveSrc = currentSrc

    if (effectiveSrc.isNullOrBlank() || hasError) {
        if (hasError) {
            console.warn("[SafeImg] Failed to load image: $effectiveSrc")
        }
        Span({
            if (modifier != null) style(modifier)
            style {
                property("display", "inline-flex")
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
        }) {
            LucideIcon(
                name = fallbackIcon,
                size = fallbackSize,
                color = "var(--muted)"
            )
        }
    } else {
        Img(src = effectiveSrc, alt = alt) {
            attr("loading", loading)
            attr("decoding", "async")
            if (modifier != null) style(modifier)
            
            // Native error handler for Compose HTML
            addEventListener("error") {
                if (effectiveSrc != fallbackSrc && !fallbackSrc.isNullOrBlank()) {
                    console.warn("[SafeImg] Error loading $effectiveSrc, falling back to $fallbackSrc")
                    currentSrc = fallbackSrc
                } else {
                    console.error("[SafeImg] Image error event triggered: $effectiveSrc")
                    hasError = true
                }
            }
        }
    }
}
